/**
 * Sortable table header cell
 *
 * Extends Th with click-to-sort behavior and ascending/descending indicators.
 */

import { cn } from '@/lib/infra/utils';
import { ArrowUp, ArrowDown, ArrowUpDown, Pin, PinOff } from 'lucide-react';

export type SortDirection = 'asc' | 'desc' | null;

interface SortableThProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  children: React.ReactNode;
  sortKey: string;
  activeSortKey: string | null;
  activeSortDir: SortDirection;
  onSort: (key: string) => void;
  className?: string;
  /** Optional: when provided, an extra pin button appears next to the
   *  sort-direction arrow on the ACTIVE column. Clicking it calls onPin()
   *  with the current sortKey + sortDir, typically wired to a persisted
   *  "default sort" setter. `isPinned=true` fills the pin (default
   *  matches active sort); otherwise it's outlined (default differs). */
  isPinned?: boolean;
  /** Fired when the trader clicks the pin. Caller decides whether to
   *  save the sort as default or clear an existing default. */
  onPin?: () => void;
  /** #902 — false renders the same header with no sort affordance at all: no
   *  click, no visible arrow, no pin. For a table whose row order is supplied
   *  by its caller, where an arrow would claim an order the rows are not in.
   *  The unsorted arrow's space is kept (invisible), so the column is exactly
   *  as wide as on an ordinary board and the rows beneath it do not re-wrap.
   *  Defaults to true, so every existing header is unchanged. */
  sortable?: boolean;
  /** Multi-column sort (2026-09-27): this column's place in the sort chain (1 = primary). Shown only when set. */
  sortRank?: number;
}

const HEADER_TEXT = 'whitespace-nowrap px-3 py-3 text-left text-[13px] font-semibold uppercase tracking-wider text-muted-foreground';

export function SortableTh({
  children,
  sortKey,
  activeSortKey,
  activeSortDir,
  onSort,
  className,
  isPinned,
  onPin,
  sortable = true,
  sortRank,
  ...rest
}: SortableThProps) {
  const isActive = activeSortKey === sortKey;
  const showPin = isActive && onPin != null;

  if (!sortable) {
    return (
      <th {...rest} className={cn(HEADER_TEXT, className)}>
        <span className="inline-flex items-center gap-1">
          {children}
          <ArrowUpDown aria-hidden="true" className="invisible h-3 w-3" />
        </span>
      </th>
    );
  }

  return (
    <th
      {...rest}
      className={cn(
        HEADER_TEXT,
        'hover:text-foreground cursor-pointer select-none transition-colors',
        className,
        // The active sort reads at a glance (Stuart, 2026-10-25): a light blue label and arrow on a faint blue tint.
        isActive && 'bg-sky-400/5 text-sky-600 hover:text-sky-700 dark:text-sky-300 dark:hover:text-sky-200',
      )}
      data-sort-active={isActive ? 'true' : undefined}
      onClick={() => onSort(sortKey)}
    >
      {/* The label may shrink (clipped by a fixed-width cell's overflow) while the arrow and rank keep their size, so a
          sorted header stays inside its own column (Stuart, 2026-10-26: SPORT ⇅ MARKET ↓ ran together). */}
      {/* The shrunk label clips instead of running under its arrow (Stuart, 2026-10-27: the Rollup's CONTRACTS read
          CONTRAC↓S, the arrow painted over the T). */}
      <span className="inline-flex max-w-full items-center gap-1">
        <span className="min-w-0 overflow-hidden">{children}</span>
        {isActive && activeSortDir === 'asc' && <ArrowUp className="h-3 w-3 shrink-0" />}
        {isActive && activeSortDir === 'desc' && <ArrowDown className="h-3 w-3 shrink-0" />}
        {!isActive && <ArrowUpDown className="h-3 w-3 shrink-0 opacity-30" />}
        {isActive && sortRank != null && (
          <span className="text-primary -ml-0.5 shrink-0 font-mono text-[9px] leading-none" aria-label={`sort ${sortRank}`}>
            {sortRank}
          </span>
        )}
        {showPin && (
          // Stop propagation so clicking the pin doesn't also re-trigger
          // onSort (which would flip the direction). Filled when the
          // current sort already matches the persisted default; outlined
          // when not — click toggles the pin state via the parent's
          // onPin handler.
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPin?.();
            }}
            title={
              isPinned
                ? 'This sort is your default. Click to clear the default.'
                : 'Save this sort as your default (persists across reloads).'
            }
            aria-label={isPinned ? 'Unpin default sort' : 'Pin as default sort'}
            className={cn(
              'ml-0.5 rounded p-0.5 transition-colors',
              isPinned
                ? 'text-primary hover:bg-muted'
                : 'text-muted-foreground/60 hover:bg-muted hover:text-foreground',
            )}
          >
            {isPinned ? <Pin className="h-3 w-3" /> : <PinOff className="h-3 w-3" />}
          </button>
        )}
      </span>
    </th>
  );
}
